import { motion } from "framer-motion";
import { Bus, Car, CarFront, Gauge, Zap } from "lucide-react";
import { FLEET, wa, slug } from "../lib/site";
import { MaskLines, SectionEyebrow } from "./Reveal";

const ICONS = { bus: Bus, suv: CarFront, car: Car };
const inr = (n) => n.toLocaleString("en-IN");

const VehicleCard = ({ v, i }) => {
  const Icon = ICONS[v.icon];
  return (
    <motion.article
      initial={{ opacity: 0, y: 50, rotate: i % 2 ? 2 : -2 }}
      whileInView={{ opacity: 1, y: 0, rotate: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.08 }}
      whileHover={{ y: -8 }}
      className="group relative flex flex-col rounded-[28px] bg-white p-6 sm:p-7 shadow-[0_20px_50px_-25px_rgba(0,0,0,0.5)]"
      data-testid={`fleet-card-${slug(v.name)}`}
    >
      <div className="flex items-start justify-between">
        <span className="rounded-full bg-ink px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-white">{v.type}</span>
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-go/15 text-go transition-[transform,background-color,color] duration-500 group-hover:-rotate-12 group-hover:bg-go group-hover:text-ink">
          <Icon size={28} strokeWidth={2.2} />
        </span>
      </div>
      <h3 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-ink">{v.name}</h3>
      <div className="mt-4 flex items-baseline gap-1 text-ink" data-testid={`fleet-rate-${slug(v.name)}`}>
        <span className="font-display text-7xl font-extrabold leading-none tracking-[-0.05em]">₹{v.rate}</span>
        <span className="font-mono text-sm font-bold text-zinc-600">/ km</span>
      </div>
      <div className="my-6 border-t-2 border-dashed border-zinc-200 relative">
        <span className="absolute -left-10 -top-3 h-6 w-6 rounded-full bg-go" />
        <span className="absolute -right-10 -top-3 h-6 w-6 rounded-full bg-go" />
      </div>
      <p className="flex items-center gap-2 text-sm text-zinc-700">
        <Gauge size={16} className="text-go" /> Min. day billing: <b className="text-ink">₹{inr(v.rate * 300)}</b> (300 km)
      </p>
      <a href={wa(`Hi, I want to book the ${v.type} – ${v.name} (₹${v.rate}/km). Please share availability.`)}
        target="_blank" rel="noopener noreferrer" data-testid={`fleet-book-${slug(v.name)}`}
        className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-ink py-3.5 font-bold text-white transition-[background-color,color] duration-300 hover:bg-go hover:text-ink">
        <Zap size={16} /> Instant Booking
      </a>
    </motion.article>
  );
};

export const Fleet = () => (
  <section id="fleet" className="relative overflow-hidden bg-go py-24 sm:py-32" data-testid="fleet-section">
    <div className="pointer-events-none absolute inset-0 grain opacity-60" />
    <span className="pointer-events-none absolute -right-10 top-10 select-none font-display text-[22vw] font-extrabold leading-none text-ink/[0.07]">FLEET</span>
    <div className="relative mx-auto max-w-7xl px-5">
      <SectionEyebrow index="02" label="Fleet & Vehicle Rentals" indexClass="text-ink" />
      <div className="mt-5 grid gap-8 lg:grid-cols-12 lg:items-end">
        <h2 className="lg:col-span-7 font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.92] tracking-[-0.035em] text-ink">
          <MaskLines lines={["Premium Vehicle", "Rentals."]} />
        </h2>
        <div className="lg:col-span-5 flex items-center gap-4 rounded-2xl border-2 border-ink bg-ink px-5 py-4 text-white" data-testid="fleet-billing-notice">
          <Gauge className="h-8 w-8 shrink-0 text-go" />
          <p className="text-sm sm:text-base font-semibold">Minimum billing criteria: <span className="text-go">300 km per day</span> for all vehicles.</p>
        </div>
      </div>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {FLEET.map((v, i) => <VehicleCard key={v.name} v={v} i={i} />)}
      </div>
    </div>
  </section>
);
